import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { Urls } from "src/constants";
import { ApiLink, UserFavoriteItem, UserFavoriteSearch } from "src/models";
import { HttpService, _ } from "src/services";
import {
  MessageModalsAction,
  ModalsAction,
  ProfileDataAction,
} from "../actions";
import { ReduxAction, ProfileData } from "../models";
import { ProfileDataTypes } from "../types";

export function* ProfileDataSaga() {
  yield takeLeading(
    ProfileDataTypes.REQUEST_PACKAGE_SUMMARIES,
    getPackageSummaries
  );
  yield takeLeading(ProfileDataTypes.REQUEST_LOAD_USER_PACKAGES, getPackages);
  yield takeLatest(ProfileDataTypes.GET_USER_DEVICES, getDevices);
  yield takeLeading(ProfileDataTypes.REQUEST_ACCESS_CODE, sendAccessCode);
  yield takeLeading(
    ProfileDataTypes.CONNECT_CURRENT_DEVICE_CONFIRM_CODE,
    connectCurrentDevice
  );
  yield takeLeading(
    ProfileDataTypes.CUT_ALL_DEVICE_CONFIRM_CODE,
    cutOtherDevice
  );
  yield takeLeading(ProfileDataTypes.REQUEST_USER_FAVORITES, getFavorites);
  yield takeLatest(ProfileDataTypes.SEARCH_FOR_FAVORITES, searchFavorites);
  yield takeLatest(ProfileDataTypes.ADD_FAVORITE_ITEM, addFavoriteItem);
  yield takeLatest(ProfileDataTypes.REMOVE_FAVORITE_ITEM, removeFavoriteItem);
}

function* removeFavoriteItem(action: ReduxAction) {
  const item: UserFavoriteSearch = action.payload;
  try {
    const link = item._links.find((x) => x.rel === "remove");

    if (!link) {
      yield put(ProfileDataAction.clearFavoriteActionLoading());
      return;
    }

    yield HttpService.Fetch(Urls.packApi(null, link.href, link.method, false));
    yield put(ProfileDataAction.getFavorites(false));
  } catch (error: any) {
    yield put(ProfileDataAction.clearFavoriteActionLoading());
    const axiosError: AxiosError = error;

    if (!axiosError.response || !axiosError.response.data) {
      toast.error(_("errorOccured"));
    }
  }
}

function* addFavoriteItem(action: ReduxAction) {
  const item: UserFavoriteSearch | UserFavoriteItem = action.payload;
  try {
    const link = item._links.find((x) => x.rel === "add");

    if (!link) {
      yield put(ProfileDataAction.clearFavoriteActionLoading());
      return;
    }

    yield HttpService.Fetch(Urls.packApi(null, link.href, link.method, false));
    yield put(ProfileDataAction.getFavorites(false));
  } catch (error: any) {
    yield put(ProfileDataAction.clearFavoriteActionLoading());
    const axiosError: AxiosError = error;

    if (!axiosError.response || !axiosError.response.data) {
      toast.error(_("errorOccured"));
    }
  }
}

function* searchFavorites(action: ReduxAction) {
  const { link, search }: { link: ApiLink; search?: string } = action.payload;
  try {
    const url = `${link.href}${search || ""}`;
    const response: AxiosResponse = yield HttpService.Fetch(
      Urls.packApi(null, url, link.method, false)
    );
    yield put(ProfileDataAction.setFavoritesSearchResult(response.data));
  } catch {
    yield put(ProfileDataAction.setFavoritesSearchResult([]));
  }
}

function* getFavorites() {
  const api = Urls.profile.favorites();
  try {
    const response: AxiosResponse = yield HttpService.Fetch(api);
    yield put(ProfileDataAction.setFavorites(response.data));
  } catch (error: any) {
    yield put(ProfileDataAction.setFavorites([]));
    const axiosError: AxiosError = error;

    if (!axiosError.response || !axiosError.response.data) {
      toast.error(_("errorOccured"));
    }
  } finally {
    yield put(ProfileDataAction.clearFavoriteActionLoading());
  }
}

function* cutOtherDevice(action: ReduxAction) {
  const code = action.payload;

  const api = Urls.profile.devices.cutAllConfirmCode();

  try {
    const user: ProfileData = yield select(
      (state: any) => state.ProfileDataState
    );
    const onSuccess = user.deviceToTerminate.onSuccessCallback;

    yield put(ModalsAction.showDeviceAccess());
    const response: AxiosResponse = yield HttpService.Fetch(api, { code });
    yield put(ProfileDataAction.clearAccessCode());
    yield put(ProfileDataAction.accessToggled(false));
    yield put(ModalsAction.hideDeviceAccess());

    onSuccess && onSuccess();
    if (!onSuccess && response.data && response.data.message) {
      const { message, title } = response.data;
      yield put(
        MessageModalsAction.showMessage(
          message,
          title,
          "artwork-simultaneous.svg"
        )
      );
    }
  } catch (error: any) {
    yield put(ProfileDataAction.accessNotToggled());
    const axiosError: AxiosError = error;

    if (!axiosError.response || !axiosError.response.data) {
      toast.error(_("errorOccured"));
    }
  }
}

function* connectCurrentDevice(action: ReduxAction) {
  const code = action.payload;

  const api = Urls.profile.devices.connectCurrectConfirmCode();

  try {
    const user: ProfileData = yield select(
      (state: any) => state.ProfileDataState
    );
    const onSuccess = user.deviceToTerminate.onSuccessCallback;

    yield put(ModalsAction.showDeviceAccess());
    const response: AxiosResponse = yield HttpService.Fetch(api, { code });
    yield put(ProfileDataAction.clearAccessCode());
    yield put(ProfileDataAction.accessToggled(true));
    yield put(ModalsAction.hideDeviceAccess());

    onSuccess && onSuccess();

    if (!onSuccess && response.data && response.data.message) {
      const { message, title } = response.data;
      yield put(
        MessageModalsAction.showMessage(
          message,
          title,
          "artwork-simultaneous.svg"
        )
      );
    }
  } catch (error: any) {
    yield put(ProfileDataAction.accessNotToggled());
    const axiosError: AxiosError = error;

    if (!axiosError.response || !axiosError.response.data) {
      toast.error(_("errorOccured"));
    }
  }
}

function* sendAccessCode(action: ReduxAction) {
  const currentDevice = action.payload;

  const api = currentDevice
    ? Urls.profile.devices.connectCurrentSendCode()
    : Urls.profile.devices.cutAllSendCode();

  try {
    yield put(ModalsAction.showDeviceAccess());
    const response: AxiosResponse = yield HttpService.Fetch(api);
    yield put(ProfileDataAction.accessCodeSent(response.data));
  } catch {
    toast.error(_("errorOccured"));
    yield put(ProfileDataAction.clearAccessCode());
    yield put(ModalsAction.hideDeviceAccess());
  }
}

function* getDevices(action: ReduxAction) {
  const api = Urls.profile.devices.list(action.payload || 1);
  try {
    const response: AxiosResponse = yield HttpService.Fetch(api);
    yield put(
      ProfileDataAction.setDevices(
        response.data?.list || [],
        response.data?.page,
        response.data?.pages
      )
    );
  } catch {
    yield put(ProfileDataAction.setDevices([], 0, 0));
  }
}

function* getPackages(action: ReduxAction) {
  const { count, offset } = action.payload;
  const api = Urls.profile.packageHistory(count, offset);

  try {
    const response: AxiosResponse = yield HttpService.Fetch(api);

    let totalCount = response.headers["x-total-count"];
    totalCount =
      totalCount === null || totalCount === undefined ? -1 : totalCount;

    const list = response.data || [];

    yield put(ProfileDataAction.setUserPackages(list, count, totalCount));
  } catch {
    yield put(ProfileDataAction.setUserPackages([], count, undefined));
  }
}

function* getPackageSummaries() {
  try {
    const api = Urls.profile.packageSummaries();
    const response: AxiosResponse = yield HttpService.Fetch(api);
    if (response.data) {
      yield put(
        ProfileDataAction.setPackageSummaries(
          response.data.myPackages,
          response.data.otherPackages
        )
      );
    }
  } catch {}
}
